import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react"
import clsx from "clsx"
import axios from "axios"
import { parseCookies } from "nookies"
import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import {
  GET_WORK_DIARY_BY_PROJECT,
  PROJECT_BY_ID_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import useTranslator from "@pma/hooks/use-translator"
import { Button } from "../common/button"
import { Switch } from "../common/switch"
import WorkDiaryWeather from "../projects/work-diary-weather"
import WorkDiaryTask from "../projects/work-diary-task"
import WorkDiaryResources from "../projects/work-diary-resources"
import { cn } from "@pma/utils/helpers"

interface SelectWorkDiaryListProps {
  setSelectedDiaryId: Dispatch<SetStateAction<any>>
  selectedDairyId: any
}

// Approval badge component (mobile)
const ApprovalBadge = ({
  approved,
  translate,
}: {
  approved: boolean
  translate: (key: string) => string
}) => (
  <span
    className={clsx(
      "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium sm:text-xs",
      approved
        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
        : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    )}
  >
    {approved ? translate("Approved") : translate("Not Approved")}
  </span>
)

// Approval icon (desktop)
const ApprovalIcon = ({ approved }: { approved: boolean }) => (
  <div className='flex items-center justify-center'>
    <img
      src={`${process.env.PUBLIC_URL}/${approved ? "approved" : "disapproved"}.png`}
      alt='Approval status'
      className='h-5 w-5 rounded-full'
    />
  </div>
)

// Contractor info component
const ContractorInfo = ({ contractor, size = "md" }: { contractor: any; size?: "sm" | "md" }) => {
  const imgSize = size === "sm" ? "h-8 w-8 lg:h-[35px] lg:w-[35px]" : "h-10 w-10"
  return (
    <div
      className={clsx("flex items-center gap-2", size === "sm" && "max-w-[200px] lg:max-w-[250px]")}
    >
      <img
        alt=''
        src={contractor?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
        className={clsx(imgSize, "shrink-0 rounded-full object-cover")}
      />
      <div className='flex flex-col overflow-hidden'>
        <span
          className={clsx(
            "truncate font-medium capitalize",
            size === "sm" ? "text-xs lg:text-sm" : "text-sm",
          )}
        >
          {contractor?.name || "---"}
        </span>
        <small className='truncate text-slate-400'>{contractor?.email}</small>
      </div>
    </div>
  )
}

// Table skeleton row
const TableSkeletonRow = () => (
  <tr className='border-t bg-white dark:bg-gray-900'>
    <td className='px-3 py-3 lg:px-4'>
      <Skeleton circle width={36} height={20} />
    </td>
    <td className='px-3 py-3 lg:px-4'>
      <div className='flex items-center gap-2'>
        <Skeleton circle width={35} height={35} />
        <div className='flex flex-col gap-1'>
          <Skeleton width={100} height={16} />
          <Skeleton width={130} height={12} />
        </div>
      </div>
    </td>
    <td className='px-3 py-3 lg:px-4'>
      <Skeleton width={60} />
    </td>
    <td className='px-3 py-3 lg:px-4'>
      <Skeleton width={80} />
    </td>
    <td className='px-3 py-3 lg:px-4'>
      <Skeleton circle width={20} height={20} className='mx-auto' />
    </td>
    <td className='px-3 py-3 lg:px-4'>
      <Skeleton circle width={20} height={20} className='mx-auto' />
    </td>
    <td className='px-3 py-3 lg:px-4'>
      <Skeleton width={120} />
    </td>
    <td className='px-3 py-3 lg:px-4'>
      <Skeleton width={32} height={24} />
    </td>
    <td className='px-3 py-3 lg:px-4'>
      <Skeleton width={48} height={24} />
    </td>
    <td className='px-3 py-3 lg:px-4'>
      <Skeleton width={48} height={24} />
    </td>
    <td className='px-3 py-3 lg:px-4'>
      <Skeleton borderRadius={20} width={80} height={24} />
    </td>
  </tr>
)

// Mobile card skeleton
const CardSkeleton = () => (
  <div className='rounded-lg border bg-white p-3 shadow-sm dark:bg-gray-900 sm:p-4'>
    <div className='mb-3 flex items-center justify-between border-b pb-3'>
      <Skeleton width={36} height={20} />
      <Skeleton width={120} />
    </div>
    <div className='mb-3 flex items-center gap-2'>
      <Skeleton circle width={40} height={40} />
      <div className='flex flex-1 flex-col gap-1'>
        <Skeleton width='75%' height={16} />
        <Skeleton width='50%' height={12} />
      </div>
    </div>
    <div className='space-y-2'>
      <Skeleton width='60%' />
      <Skeleton width='80%' />
      <Skeleton borderRadius={20} width={80} height={24} />
      <div className='flex gap-3 pt-2'>
        <Skeleton borderRadius={20} width={70} height={20} />
        <Skeleton borderRadius={20} width={70} height={20} />
      </div>
      <div className='grid grid-cols-3 gap-2 border-t pt-3'>
        {[1, 2, 3].map((i) => (
          <div key={i} className='flex flex-col items-center gap-1'>
            <Skeleton width={50} height={12} />
            <Skeleton width={32} height={32} />
          </div>
        ))}
      </div>
    </div>
  </div>
)

const SelectWorkDiaryList = ({ setSelectedDiaryId, selectedDairyId }: SelectWorkDiaryListProps) => {
  const { projectId } = useParams()
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const popoverRef = useRef<HTMLDivElement>(null)

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [id, setId] = useState<any>(null)

  // Close on outside click
  useEffect(() => {
    if (!isDialogOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsDialogOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isDialogOpen])

  const { data: project } = useQuery({
    queryKey: ["get-project", projectId],
    queryFn: () =>
      axios
        .get(`${SERVER_URL}${PROJECT_BY_ID_ENDPOINT}/${projectId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        .then((res) => res.data),
    enabled: !!projectId,
  })

  const { data: workDiaryList, isLoading } = useQuery({
    queryKey: ["work-diaries-context", projectId],
    queryFn: () =>
      axios
        .post(
          SERVER_URL + GET_WORK_DIARY_BY_PROJECT,
          {
            project_id: projectId,
            paging_start_page: 1,
            paging_per_page: 1000,
            show_deleted: false,
          },
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          },
        )
        .then((res) => res.data),
    enabled: !!projectId,
  })

  const formatDate = (date: string) => (isHebrew ? date.split("-").reverse().join("-") : date)
  const getStatusText = (status: any) => (isHebrew ? status?.he : status?.en)
  const handleToggle = (checked: boolean, diaryId: any) => setId(checked ? diaryId : null)
  const isDisabled = (diaryId: any) => id != null && id !== diaryId

  const openDialog = () => {
    setId(selectedDairyId)
    setIsDialogOpen(true)
  }

  const confirmSelection = () => {
    setSelectedDiaryId(id)
    setIsDialogOpen(false)
  }

  const diaries = workDiaryList?.items || []
  const tableHeaders = [
    "Select",
    "Contractor",
    "Log Number",
    "Date",
    "Contractor Approval",
    "Supervisor Approval",
    "Address",
    "Weather",
    "Task",
    "Resources",
    "Status",
  ]

  return (
    <>
      {/* Trigger Button */}
      <div className='relative inline-block text-left'>
        <button
          onClick={openDialog}
          className='relative inline-flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-[5px] text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto sm:text-sm'
        >
          {selectedDairyId ? `1 ${translate("selected diary")}` : translate("Select Diary")}
          {selectedDairyId && (
            <div className='absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-600' />
          )}
        </button>
      </div>

      {/* Dialog */}
      {isDialogOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          <div
            ref={popoverRef}
            className='relative flex w-full max-w-[95vw] flex-col rounded-lg bg-white shadow-2xl dark:bg-gray-900 sm:max-w-2xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl'
          >
            {/* Header */}
            <div className='flex shrink-0 items-center justify-between border-b px-4 py-3 sm:px-6'>
              <h3 className='text-lg font-bold sm:text-xl'>{translate("Work Diary List")}</h3>
              <button
                onClick={() => setIsDialogOpen(false)}
                className='rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800'
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-5 w-5'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className='min-h-0 flex-1 overflow-hidden p-4 sm:p-6'>
              {/* Desktop Table */}
              <div className='hidden h-full md:block'>
                <div className='h-full max-h-[60vh] overflow-auto rounded-lg border'>
                  <table className='w-full min-w-[1100px] table-auto'>
                    <thead className='sticky top-0 z-10 bg-gray-100 dark:bg-gray-800'>
                      <tr>
                        {tableHeaders.map((header) => (
                          <th
                            key={header}
                            className='px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 lg:px-4 lg:text-sm'
                          >
                            {translate(header)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        Array.from({ length: 5 }).map((_, i) => <TableSkeletonRow key={i} />)
                      ) : diaries.length ? (
                        diaries.map((diary: any) => (
                          <tr
                            key={diary.id}
                            className={cn(
                              "border-t bg-white transition-colors hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800",
                              diary.deleted ? "bg-red-100" : "",
                            )}
                          >
                            <td className='px-3 py-3 lg:px-4'>
                              <Switch
                                disabled={isDisabled(diary.id)}
                                checked={id === diary.id}
                                onCheckedChange={(c) => handleToggle(c, diary.id)}
                              />
                            </td>
                            <td className='px-3 py-3 lg:px-4'>
                              <ContractorInfo contractor={diary._contractor} size='sm' />
                            </td>
                            <td className='px-3 py-3 lg:px-4'>
                              <small className={cn("truncate text-nowrap")}>{diary.number}</small>
                            </td>
                            <td className='px-3 py-3 lg:px-4'>
                              <small className='truncate text-nowrap'>
                                {formatDate(diary.diary_date)}
                              </small>
                            </td>
                            <td className='px-3 py-3 lg:px-4'>
                              <ApprovalIcon approved={diary.contractor_approval} />
                            </td>
                            <td className='px-3 py-3 lg:px-4'>
                              <ApprovalIcon approved={diary.supervisor_approval} />
                            </td>
                            <td className='px-3 py-3 lg:px-4'>
                              <p className='mb-0 min-h-6 max-w-[20ch] truncate text-xs text-slate-500 lg:max-w-[30ch] lg:text-sm'>
                                {diary.address || "---"}
                              </p>
                            </td>
                            <td className='px-3 py-3 lg:px-4'>
                              <WorkDiaryWeather workDiaryDetails={diary} />
                            </td>
                            <td className='px-3 py-3 lg:px-4'>
                              <WorkDiaryTask workDiaryDetails={diary} />
                            </td>
                            <td className='px-3 py-3 lg:px-4'>
                              <WorkDiaryResources workDiaryDetails={diary} />
                            </td>
                            <td className='px-3 py-3 lg:px-4'>
                              <Button
                                variant='default'
                                className='h-6 rounded-full text-[10px] text-black lg:text-[11px]'
                                style={{ background: diary?._status?.color }}
                              >
                                {getStatusText(diary._status)}
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={11} className='px-4 py-8 text-center text-sm text-gray-500'>
                            {translate("No work diaries found")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Cards */}
              <div className='block max-h-[60vh] space-y-3 overflow-y-auto md:hidden'>
                {isLoading ? (
                  Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)
                ) : diaries.length ? (
                  diaries.map((diary: any) => (
                    <div
                      key={diary.id}
                      className={clsx(
                        "rounded-lg border bg-white p-3 shadow-sm dark:bg-gray-900 sm:p-4",
                        isHebrew ? "text-right" : "text-left",
                      )}
                    >
                      <div className='mb-3 flex items-center justify-between border-b pb-3'>
                        <Switch
                          disabled={isDisabled(diary.id)}
                          checked={id === diary.id}
                          onCheckedChange={(c) => handleToggle(c, diary.id)}
                        />
                        <span className='text-xs font-semibold text-gray-600 dark:text-gray-300 sm:text-sm'>
                          {project?.title || "---"}
                        </span>
                      </div>
                      <ContractorInfo contractor={diary._contractor} />
                      <div className='mt-3 space-y-2 text-xs text-gray-800 dark:text-gray-200 sm:text-sm'>
                        <div>
                          <strong>{translate("Log Number")}:</strong> {diary.number || "---"}
                        </div>
                        <div>
                          <strong>{translate("Address")}:</strong>{" "}
                          <span className='truncate'>{diary.address || "---"}</span>
                        </div>
                        <div className='flex items-center gap-2'>
                          <strong>{translate("Status")}:</strong>
                          <span
                            className='rounded-full px-2 py-0.5 text-[10px] text-black'
                            style={{ background: diary?._status?.color }}
                          >
                            {getStatusText(diary._status)}
                          </span>
                        </div>
                        <div className='flex flex-wrap gap-3 pt-2'>
                          <div className='flex items-center gap-2'>
                            <strong className='text-[10px] sm:text-xs'>
                              {translate("Contractor Approval")}:
                            </strong>
                            <ApprovalBadge
                              approved={diary.contractor_approval}
                              translate={translate}
                            />
                          </div>
                          <div className='flex items-center gap-2'>
                            <strong className='text-[10px] sm:text-xs'>
                              {translate("Supervisor Approval")}:
                            </strong>
                            <ApprovalBadge
                              approved={diary.supervisor_approval}
                              translate={translate}
                            />
                          </div>
                        </div>
                        <div className='grid grid-cols-3 gap-2 border-t pt-3'>
                          {[
                            { label: "Weather", Component: WorkDiaryWeather },
                            { label: "Task", Component: WorkDiaryTask },
                            { label: "Resources", Component: WorkDiaryResources },
                          ].map(({ label, Component }) => (
                            <div
                              key={label}
                              className='flex flex-col items-center rounded-lg bg-gray-50 p-2 dark:bg-gray-800'
                            >
                              <strong className='mb-1 text-[10px] text-gray-500 dark:text-gray-400 sm:text-xs'>
                                {translate(label)}
                              </strong>
                              <Component workDiaryDetails={diary} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className='py-8 text-center text-sm text-gray-500'>
                    {translate("No work diaries found")}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className='flex shrink-0 items-center justify-end gap-2 border-t px-4 py-3 sm:px-6'>
              <button
                onClick={() => setIsDialogOpen(false)}
                className='rounded-md bg-gray-100 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 sm:px-4 sm:text-sm'
              >
                {translate("Cancel")}
              </button>
              <button
                onClick={confirmSelection}
                disabled={!id}
                className='rounded-md bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm'
              >
                {translate("Assign")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SelectWorkDiaryList
